import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { SelectField, SwitchField, TextField } from '../components/fields'
import { useAdminContext } from '../session'

interface MailServer {
  is_enabled: boolean
  host: string
  port: number
  security: 'starttls' | 'ssl' | 'none'
  username: string | null
  has_password: boolean
  password_unreadable: boolean
  from_email: string
  from_name: string
  reply_to: string | null
  updated_at: string | null
  active_source: 'database' | 'environment' | 'console'
}

interface Route {
  id: string
  category: string
  email: string
  label: string | null
  locale: 'de' | 'fr' | 'en'
  is_active: boolean
}

interface MailTest {
  ok: boolean
  status: string
  detail: string | null
}

const LOCALES = ['fr', 'de', 'en'] as const

/**
 * Notifications e-mail de l'équipe.
 *
 * Deux questions, deux cartes : **par où partent les e-mails** (le serveur
 * d'envoi) et **qui reçoit quoi** (une catégorie d'alerte → des adresses).
 * Chaque carte a son bouton d'essai : on apprend qu'un mot de passe est faux
 * ou qu'une adresse est mal tapée avant la première vente, pas après.
 */
export function AdminNotificationsPage() {
  const { t } = useTranslation()
  const { eventId, can } = useAdminContext()

  useEffect(() => {
    document.title = t('admin.section.notifications')
  }, [t])

  return (
    <>
      <AdminHeader
        title={t('admin.section.notifications')}
        description={t('admin.mail.description')}
      />
      <div className="space-y-8">
        <RoutesCard eventId={eventId} canEdit={can('admin.notification.update')} canSend={can('admin.notification.send')} />
        <ServerCard eventId={eventId} canEdit={can('admin.mail_server.update')} canSend={can('admin.notification.send')} />
      </div>
    </>
  )
}

// ── Qui reçoit quoi ────────────────────────────────────────────────

function RoutesCard({
  eventId,
  canEdit,
  canSend,
}: {
  eventId: string | undefined
  canEdit: boolean
  canSend: boolean
}) {
  const { t } = useTranslation()
  const categories = useAdminQuery<{ code: string }[]>(
    ['admin-route-categories'],
    '/admin/notifications/routes/categories',
    eventId,
  )
  const routes = useAdminQuery<Route[]>(['admin-routes'], '/admin/notifications/routes', eventId)
  const invalidate = [['admin-routes']]

  const create = useAdminMutation<Route, Omit<Route, 'id' | 'is_active'>>(
    'post',
    () => '/admin/notifications/routes',
    eventId,
    invalidate,
  )
  const update = useAdminMutation<Route, Partial<Route> & { id: string }>(
    'patch',
    (variables) => `/admin/notifications/routes/${variables.id}`,
    eventId,
    invalidate,
  )
  const remove = useAdminMutation<unknown, { id: string }>(
    'delete',
    (variables) => `/admin/notifications/routes/${variables.id}`,
    eventId,
    invalidate,
  )
  const test = useAdminMutation<MailTest, { id: string }>(
    'post',
    (variables) => `/admin/notifications/routes/${variables.id}/test`,
    eventId,
  )
  const [results, setResults] = useState<Record<string, MailTest>>({})

  const error = create.error ?? update.error ?? remove.error ?? test.error

  return (
    <Card className="space-y-6">
      <div>
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.mail.routesTitle')}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-content-secondary">{t('admin.mail.routesHint')}</p>
      </div>

      {categories.isPending || routes.isPending ? <Skeleton className="h-40 w-full" /> : null}
      {routes.isError ? <p className="text-sm text-state-danger">{routes.error.message}</p> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {(categories.data ?? []).map(({ code }) => {
          const subscribed = (routes.data ?? []).filter((route) => route.category === code)
          return (
            <section key={code} className="rounded-lg border border-line-subtle p-4">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-sm font-medium text-content-primary">
                  {t(`admin.mail.category.${code}.title`)}
                </h3>
                <span className="text-caption tabular-nums text-content-muted">
                  {t('admin.mail.recipients', { count: subscribed.filter((r) => r.is_active).length })}
                </span>
              </div>
              <p className="mt-1 text-xs text-content-muted">
                {t(`admin.mail.category.${code}.hint`)}
              </p>

              <ul className="mt-3 space-y-2">
                {subscribed.map((route) => (
                  <li
                    key={route.id}
                    className={cn(
                      'rounded-md border border-line-subtle px-3 py-2',
                      !route.is_active && 'opacity-60',
                    )}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm text-content-primary">{route.email}</p>
                        {route.label ? (
                          <p className="truncate text-xs text-content-muted">{route.label}</p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Badge>{route.locale.toUpperCase()}</Badge>
                        {canEdit ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => update.mutate({ id: route.id, is_active: !route.is_active })}
                          >
                            {route.is_active ? t('admin.mail.pause') : t('admin.mail.resume')}
                          </Button>
                        ) : null}
                        {canSend ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={test.isPending}
                            onClick={() =>
                              test.mutate(
                                { id: route.id },
                                {
                                  onSuccess: (result) =>
                                    setResults((current) => ({ ...current, [route.id]: result })),
                                },
                              )
                            }
                          >
                            {t('admin.mail.test')}
                          </Button>
                        ) : null}
                        {canEdit ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={t('admin.mail.remove')}
                            onClick={() => remove.mutate({ id: route.id })}
                          >
                            ✕
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    {results[route.id] ? <TestResult result={results[route.id]!} /> : null}
                  </li>
                ))}
              </ul>

              {canEdit ? (
                <AddRecipient
                  busy={create.isPending}
                  onAdd={(payload, done) => create.mutate({ ...payload, category: code }, { onSuccess: done })}
                />
              ) : null}
            </section>
          )
        })}
      </div>

      {error instanceof ApiError ? <p className="text-sm text-state-danger">{error.message}</p> : null}
    </Card>
  )
}

function AddRecipient({
  busy,
  onAdd,
}: {
  busy: boolean
  onAdd: (payload: { email: string; label: string | null; locale: Route['locale'] }, done: () => void) => void
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [label, setLabel] = useState('')
  const [locale, setLocale] = useState<Route['locale']>(
    (LOCALES as readonly string[]).includes(i18n.language) ? (i18n.language as Route['locale']) : 'fr',
  )

  if (!open) {
    return (
      <Button size="sm" variant="outline" className="mt-3" onClick={() => setOpen(true)}>
        + {t('admin.mail.add')}
      </Button>
    )
  }

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  return (
    <div className="mt-3 grid gap-3 rounded-md border border-line-subtle p-3 sm:grid-cols-2">
      <TextField label={t('admin.mail.email')} type="email" value={email} onChange={setEmail} />
      <TextField
        label={t('admin.mail.label')}
        placeholder={t('admin.mail.labelPlaceholder')}
        value={label}
        onChange={setLabel}
      />
      <SelectField
        label={t('admin.mail.locale')}
        value={locale}
        onChange={(value) => setLocale(value as Route['locale'])}
        options={LOCALES.map((code) => ({ value: code, label: t(`admin.mail.localeName.${code}`) }))}
      />
      <div className="flex items-end gap-2">
        <Button
          size="sm"
          disabled={!valid || busy}
          onClick={() =>
            onAdd({ email: email.trim(), label: label.trim() || null, locale }, () => {
              setEmail('')
              setLabel('')
              setOpen(false)
            })
          }
        >
          {t('admin.mail.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          {t('admin.cancel')}
        </Button>
      </div>
    </div>
  )
}

function TestResult({ result }: { result: MailTest }) {
  const { t } = useTranslation()
  return (
    <p className={cn('mt-2 text-xs', result.ok ? 'text-state-success' : 'text-state-danger')}>
      {result.ok ? t('admin.mail.testOk') : t(`admin.mail.testStatus.${result.status}`, { defaultValue: result.status })}
      {result.detail ? ` — ${result.detail}` : ''}
    </p>
  )
}

// ── Serveur d'envoi ────────────────────────────────────────────────

function ServerCard({
  eventId,
  canEdit,
  canSend,
}: {
  eventId: string | undefined
  canEdit: boolean
  canSend: boolean
}) {
  const { t } = useTranslation()
  const query = useAdminQuery<MailServer>(['admin-mail-server'], '/admin/notifications/mail-server', eventId)
  const save = useAdminMutation<MailServer, Record<string, unknown>>(
    'put',
    () => '/admin/notifications/mail-server',
    eventId,
    [['admin-mail-server']],
  )
  const check = useAdminMutation<MailTest, Record<string, never>>(
    'post',
    () => '/admin/notifications/mail-server/check',
    eventId,
  )
  const sendTest = useAdminMutation<MailTest, { recipient: string }>(
    'post',
    () => '/admin/notifications/mail-server/test',
    eventId,
  )

  const [draft, setDraft] = useState<MailServer | null>(null)
  const [password, setPassword] = useState('')
  const [recipient, setRecipient] = useState('')
  const [outcome, setOutcome] = useState<MailTest | null>(null)

  const server = draft ?? query.data ?? null
  if (query.isPending) return <Skeleton className="h-64 w-full" />
  if (!server) {
    return (
      <Card>
        <p className="text-sm text-state-danger">{query.error?.message}</p>
      </Card>
    )
  }

  function set<K extends keyof MailServer>(key: K, value: MailServer[K]) {
    setDraft({ ...(draft ?? server!), [key]: value })
  }

  const error = save.error ?? check.error ?? sendTest.error

  return (
    <Card className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.mail.serverTitle')}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-content-secondary">{t('admin.mail.serverHint')}</p>
        </div>
        <Badge tone={server.active_source === 'console' ? 'ember' : 'success'}>
          {t(`admin.mail.source.${server.active_source}`)}
        </Badge>
      </div>

      {server.active_source === 'console' ? (
        <p className="rounded-md border border-ember-400/40 bg-ember-400/5 px-4 py-3 text-sm text-ember-300">
          {t('admin.mail.consoleWarning')}
        </p>
      ) : null}
      {server.password_unreadable ? (
        <p className="text-sm text-state-warning">{t('admin.mail.passwordUnreadable')}</p>
      ) : null}

      <fieldset disabled={!canEdit || save.isPending} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <SwitchField
            label={t('admin.mail.enabled')}
            hint={t('admin.mail.enabledHint')}
            value={server.is_enabled}
            onChange={(value) => set('is_enabled', value)}
          />
        </div>
        <TextField label={t('admin.mail.host')} placeholder="smtp.gmail.com" value={server.host} onChange={(value) => set('host', value)} />
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label={t('admin.mail.port')}
            value={String(server.port)}
            onChange={(value) => set('port', Number(value.replace(/\D/g, '')) || 0)}
          />
          <SelectField
            label={t('admin.mail.security')}
            value={server.security}
            onChange={(value) => {
              const security = value as MailServer['security']
              setDraft({
                ...server,
                security,
                // Le port suit le mode quand on n'en a pas choisi d'autre.
                port: [25, 465, 587].includes(server.port)
                  ? security === 'ssl' ? 465 : security === 'starttls' ? 587 : 25
                  : server.port,
              })
            }}
            options={(['starttls', 'ssl', 'none'] as const).map((mode) => ({
              value: mode,
              label: t(`admin.mail.securityMode.${mode}`),
            }))}
          />
        </div>
        <TextField label={t('admin.mail.username')} value={server.username ?? ''} onChange={(value) => set('username', value || null)} />
        <SecretField
          label={t('admin.mail.password')}
          placeholder={server.has_password ? '••••••••' : ''}
          hint={server.has_password ? t('admin.mail.passwordKept') : t('admin.mail.passwordHint')}
          value={password}
          onChange={setPassword}
        />
        <TextField label={t('admin.mail.fromEmail')} type="email" value={server.from_email} onChange={(value) => set('from_email', value)} />
        <TextField label={t('admin.mail.fromName')} value={server.from_name} onChange={(value) => set('from_name', value)} />
        <TextField label={t('admin.mail.replyTo')} type="email" value={server.reply_to ?? ''} onChange={(value) => set('reply_to', value || null)} />
      </fieldset>

      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  is_enabled: server.is_enabled,
                  host: server.host,
                  port: server.port,
                  security: server.security,
                  username: server.username,
                  from_email: server.from_email,
                  from_name: server.from_name,
                  reply_to: server.reply_to || null,
                  // Vide = on garde le mot de passe actuel.
                  ...(password ? { password } : {}),
                },
                {
                  onSuccess: () => {
                    setDraft(null)
                    setPassword('')
                  },
                },
              )
            }
          >
            {t('admin.mail.saveServer')}
          </Button>
          {server.has_password ? (
            <Button
              variant="ghost"
              disabled={save.isPending}
              onClick={() =>
                save.mutate({
                  is_enabled: false,
                  host: server.host,
                  port: server.port,
                  security: server.security,
                  username: server.username,
                  from_email: server.from_email,
                  from_name: server.from_name,
                  reply_to: server.reply_to || null,
                  password: '',
                })
              }
            >
              {t('admin.mail.forgetPassword')}
            </Button>
          ) : null}
          <Button
            variant="outline"
            disabled={check.isPending || draft !== null}
            onClick={() => check.mutate({}, { onSuccess: setOutcome })}
          >
            {check.isPending ? t('admin.mail.checking') : t('admin.mail.check')}
          </Button>
        </div>
      ) : null}
      {draft !== null ? <p className="text-xs text-content-muted">{t('admin.mail.unsaved')}</p> : null}

      {canSend ? (
        <div className="flex flex-wrap items-end gap-2 border-t border-line-subtle pt-5">
          <TextField
            className="min-w-[16rem] flex-1"
            label={t('admin.mail.testRecipient')}
            type="email"
            value={recipient}
            onChange={setRecipient}
          />
          <Button
            variant="outline"
            disabled={sendTest.isPending || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim())}
            onClick={() => sendTest.mutate({ recipient: recipient.trim() }, { onSuccess: setOutcome })}
          >
            {t('admin.mail.sendTest')}
          </Button>
        </div>
      ) : null}

      {outcome ? <TestResult result={outcome} /> : null}
      {error instanceof ApiError ? <p className="text-sm text-state-danger">{error.message}</p> : null}
    </Card>
  )
}

/**
 * Le mot de passe SMTP, masqué par défaut.
 *
 * Il s'affichait en clair pendant la frappe : sur un écran partagé ou filmé,
 * c'est le compte d'envoi de toute l'édition qui fuit. Le bouton « afficher »
 * reste là pour vérifier un mot de passe d'application de seize lettres, qu'on
 * recopie rarement juste du premier coup.
 *
 * `autoComplete="new-password"` : sans lui, le navigateur proposait — voire
 * remplissait — le mot de passe d'ADMINISTRATION enregistré pour ce site, et
 * l'enregistrement l'envoyait au serveur SMTP à la place du bon.
 */
function SecretField({
  label,
  hint,
  placeholder,
  value,
  onChange,
}: {
  label: string
  hint?: string
  placeholder?: string
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const id = useId()
  const [visible, setVisible] = useState(false)

  return (
    <div>
      <label
        htmlFor={id}
        className="text-caption uppercase tracking-wide text-content-muted"
      >
        {label}
      </label>
      <div className="mt-1.5 flex items-center gap-2">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          placeholder={placeholder}
          autoComplete="new-password"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
          className="w-full min-w-0 rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary placeholder:text-content-muted focus:border-champagne-400/60"
        />
        <button
          type="button"
          aria-pressed={visible}
          aria-controls={id}
          aria-label={visible ? t('admin.secretField.hide') : t('admin.secretField.show')}
          onClick={() => setVisible((shown) => !shown)}
          className="shrink-0 rounded-md border border-line px-3 py-2.5 text-xs text-content-secondary transition hover:border-champagne-400/50 hover:text-content-primary disabled:opacity-50"
        >
          {visible ? t('admin.secretField.hideShort') : t('admin.secretField.showShort')}
        </button>
      </div>
      {hint ? <p className="mt-1.5 text-xs text-content-muted">{hint}</p> : null}
    </div>
  )
}
